import type { ReactNode } from 'react';

// P2b-3 Build View — shared section primitive. Bordered card with
// uppercase header on the left and an optional badge slot on the
// right (modification dot, etc.). Sections compose vertically with
// space-y-6 in the parent.

type Props = {
  title: string;
  children: ReactNode;
  // Optional content rendered to the right of the title — used by
  // commit 6 for content/date drift dots and the budget warning
  // popover anchor.
  badge?: ReactNode;
  // Optional override of the default vertical padding (Execution
  // section uses a tighter, more "form-like" treatment).
  className?: string;
  // Build View polish (Fix 3): optional anchor id so the sticky side
  // menu can scroll to / scroll-spy on this section. Renders as the
  // <section>'s id attribute. Conventionally `bv-{slug}` so the ids
  // don't collide with anything else on the page.
  id?: string;
};

export function BuildViewSection({ title, children, badge, className, id }: Props) {
  return (
    <section
      id={id}
      className={
        'rounded-md border border-slate-200 bg-white p-5 ' + (className ?? '')
      }
    >
      <header className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-xs uppercase tracking-wide font-semibold text-slate-500">
          {title}
        </h2>
        {badge ? (
          <div className="inline-flex items-center gap-2">{badge}</div>
        ) : null}
      </header>
      {children}
    </section>
  );
}

// Field-row primitive used for label + value pairs inside sections.
// Two columns at sm+ (label left, value right), stacks on narrow.
export function BuildViewField({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={
        'grid grid-cols-[140px_minmax(0,1fr)] gap-3 items-start py-1.5 ' +
        (className ?? '')
      }
    >
      <dt className="text-xs uppercase tracking-wide text-slate-500 pt-0.5">
        {label}
      </dt>
      <dd className="text-sm text-slate-800 min-w-0">{children}</dd>
    </div>
  );
}
